import MarkdownIt from "markdown-it"
import githubAlerts from "markdown-it-github-alerts"
import { parseFrontmatter } from "../src/js/utilitaireMarkdown/parseFrontmatter.js"

const mdParser = new MarkdownIt({
    html: true,
    linkify: true,
}).use(githubAlerts)

const BLOG_LIST_PATH = "/content/blog/index.json"
const BLOG_MARKDOWN_PATH = (slug) => `/content/blog/${encodeURIComponent(slug)}.md`

/**
 * Echappe une chaine pour l'inserer en securite dans du HTML.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;")
}

/**
 * Produit un resume texte brut depuis un markdown pour la meta description.
 * @param {string} markdown
 * @param {number} [max=160]
 * @returns {string}
 */
function textSummary(markdown, max = 160) {
    const text = markdown
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
        .replace(/\[[^\]]+\]\([^)]*\)/g, "$1")
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/[>*_~\-]/g, " ")
        .replace(/\s+/g, " ")
        .trim()

    if (text.length <= max) return text
    return `${text.slice(0, max - 1).trim()}...`
}

/**
 * Charge l'index des articles de blog depuis le dossier public.
 * @returns {Promise<Array<{nomFichier?: string, titre?: string}>>}
 */
async function readBlogIndex() {
    const res = await fetch(BLOG_LIST_PATH)
    if (!res.ok) return []
    return res.json()
}

/**
 * Construit le rendu pre-rendu de la page liste /blog.
 * @param {string} urlPath
 * @returns {Promise<{html: string, links: Set<string>, head: {lang: string, title: string, elements: Set<object>}}>} 
 */
async function renderBlogList(urlPath) {
    const pages = await readBlogIndex()

    const links = pages
        .map((page) => page?.nomFichier)
        .filter(Boolean)
        .map((slug) => `/blog/${slug}`)

    const items = pages
        .map((page) => {
            const titre = escapeHtml(page?.titre || page?.nomFichier || "Article")
            const slug = encodeURIComponent(page?.nomFichier || "")
            return `<li><a href="/blog/${slug}">${titre}</a></li>`
        })
        .join("\n")

    return {
        html: `
      <main class="markdown-body" id="prerender-content" data-prerender-static="blog-list">
        <h1>Liste des articles</h1>
        <nav>
          <ul>${items}</ul>
        </nav>
      </main>
    `,
        links: new Set(links),
        head: {
            lang: "fr",
            title: "Blog - Echanges pokémon",
            elements: new Set([
                { type: "meta", props: { name: "description", content: "Liste des articles du blog Echanges Pokémon" } },
                { type: "link", props: { rel: "canonical", href: `https://echangepokemons.netlify.app${urlPath}` } },
            ]),
        },
    }
}

/**
 * Construit le rendu pre-rendu d'une page article /blog/:slug.
 * @param {string} urlPath
 * @param {string} slug
 * @returns {Promise<{html: string, head: {lang: string, title: string, elements?: Set<object>}}>} 
 */
async function renderBlogArticle(urlPath, slug) {
    const res = await fetch(BLOG_MARKDOWN_PATH(slug))
    if (!res.ok) {
        return {
            html: `<main id="prerender-content"><h1>Article introuvable</h1></main>`,
            head: {
                lang: "fr",
                title: "Article introuvable",
            },
        }
    }

    const markdown = await res.text()
    const { attributes, content } = parseFrontmatter(markdown)
    const htmlContent = mdParser.render(content)

    const title = attributes.titre || attributes.title || slug.replaceAll("-", " ")
    const description = attributes.description || textSummary(content)

    return {
        html: `<main class="markdown-body" id="prerender-content" data-prerender-static="blog-article">${htmlContent}</main>`,
        head: {
            lang: "fr",
            title,
            elements: new Set([
                { type: "meta", props: { name: "description", content: description } },
                { type: "meta", props: { name: "robots", content: "index,follow" } },
                { type: "link", props: { rel: "canonical", href: `https://echangepokemons.netlify.app${urlPath}` } },
            ]),
        },
    }
}

/**
 * Point d'entree appele par vite-prerender-plugin pour chaque route.
 * @param {{ url: string }} param0
 * @returns {Promise<{html: string} | {html: string, links: Set<string>, head: object}>}
 */
export async function prerender({ url }) {
    const normalized = (url || "/").replace(/\/$/, "") || "/"

    if (normalized === "/blog") {
        return renderBlogList(normalized)
    }

    const match = normalized.match(/^\/blog\/([^/]+)$/)
    if (match) {
        const slug = decodeURIComponent(match[1])
        return renderBlogArticle(normalized, slug)
    }

    return { html: "" }
}
